import {
  arrayToString,
  buildRegex,
  createFormat,
  RegexObject,
  RegexAdditionObject,
  RegexFormat,
} from './helpers';

/**
 * List of time unit format objects to build out regex and formatStrings.
 */
const timeUnits = {
  a: createFormat('[aApP]', 'a'),
  aa: createFormat('[aApP][mM]', 'aa'),
  c: createFormat('[:]', ':'),
  h: createFormat(/^\d$/, 'h'),
  hh: createFormat(/^[0]\d$|^[1]\d$|^[2][0-3]$/, 'hh'),
  mm: createFormat('[0-5]\\d', 'mm'),
  ss: createFormat('[0-5]\\d', 'ss'),
  sp: createFormat('\\s', ' '),
};

type DateFormatTuple = [RegexObject, ...RegexAdditionObject[]];

/**
 * Create a list of time format objects.
 * @returns { Array } List of time formats [{ regex, formatString }]
 */
const getTimeFormatList = (): DateFormatTuple[] => {
  const { a, aa, c, h, hh, mm, ss, sp } = timeUnits;

  const baseValue: DateFormatTuple[] = [];
  return baseValue.concat(
    ...[h, hh].map((hour) => [
      [hour] as DateFormatTuple,
      [hour, a] as DateFormatTuple,
      [hour, aa] as DateFormatTuple,
      [hour, sp, a] as DateFormatTuple,
      [hour, sp, aa] as DateFormatTuple,
      [hour, mm] as DateFormatTuple,
      [hour, mm, a] as DateFormatTuple,
      [hour, mm, aa] as DateFormatTuple,
      [hour, mm, ss] as DateFormatTuple,
      [hour, mm, ss, a] as DateFormatTuple,
      [hour, mm, ss, aa] as DateFormatTuple,
      [hour, mm, sp, a] as DateFormatTuple,
      [hour, mm, sp, aa] as DateFormatTuple,
      [hour, mm, ss, sp, a] as DateFormatTuple,
      [hour, mm, ss, sp, aa] as DateFormatTuple,
      [hour, c, mm] as DateFormatTuple,
      [hour, c, mm, a] as DateFormatTuple,
      [hour, c, mm, aa] as DateFormatTuple,
      [hour, c, mm, ss] as DateFormatTuple,
      [hour, c, mm, ss, a] as DateFormatTuple,
      [hour, c, mm, ss, aa] as DateFormatTuple,
      [hour, c, mm, sp, a] as DateFormatTuple,
      [hour, c, mm, sp, aa] as DateFormatTuple,
      [hour, c, mm, c, ss] as DateFormatTuple,
      [hour, c, mm, c, ss, a] as DateFormatTuple,
      [hour, c, mm, c, ss, aa] as DateFormatTuple,
      [hour, c, mm, c, ss, sp, a] as DateFormatTuple,
      [hour, c, mm, c, ss, sp, aa] as DateFormatTuple,
    ]),
  );
};

/**
 * Creates an object of time formats by the length of the formatString.
 * @returns { Object } Time formats by length:
 *                     {3: [{}, {}], 4: [{}, {}]...} No Seconds
 *                     {3s: [{}, {}], 4s: [{}, {}]...} With Seconds
 */
const createTimeFormatsByLength = () => {
  const timeFormats: Record<string | number, RegexFormat[]> = {};

  getTimeFormatList().forEach((format) => {
    const regex = buildRegex(format[0], format.slice(1));
    const formatString = arrayToString(format, 'formatString');
    const timeFormat = createFormat(regex, formatString);
    const { length } = formatString;

    if (!timeFormats[length]) {
      timeFormats[length] = [];
      timeFormats[`${length}s`] = [];
    }
    if (formatString.indexOf(timeUnits.ss.formatString) === -1) {
      timeFormats[length].push(timeFormat);
    } else {
      timeFormats[`${length}s`].push(timeFormat);
    }
  });
  return timeFormats;
};
const timeFormatsByLength = createTimeFormatsByLength();

/**
 * getTimeFormatsByLength
 * Get a list of time formats of a certain length;
 * @param { Number } length: The length of the time string.
 * @param { Boolean } hasSeconds: Time should parse seconds.
 * @returns { Array } List of time format objects.
 */
export function getTimeFormatsByLength(length: number, hasSeconds?: boolean) {
  const formats = timeFormatsByLength[length] || [];
  if (hasSeconds) {
    return [...formats, ...(timeFormatsByLength[`${length}s`] || [])];
  }
  return formats;
}

/**
 * getTimeFormat
 * Get the time format from a given time input if it matches regex
 * @param { String } time: Time to test.
 * @param { Array } timeFormats: List of time format objects to test.
 * @returns { Object } Time format Object { regex, formatString }.
 */
export function getTimeFormat(
  time: string,
  timeFormats: RegexFormat[],
): RegexFormat | Record<string, never> {
  let timeFormat = {};
  timeFormats.forEach((format) => {
    if (format.regex && format.regex.test(time)) {
      timeFormat = format;
    }
  });
  return timeFormat;
}
